import { useEffect, useState } from 'react';

import { useHovered } from '../hooks/useHovered/useHovered';
import { t } from 'i18next';

import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useQuestsStore } from 'shared/store/questsStore';
import { useStartGameStore } from 'shared/store/start-game-store';
import { CurrencyType } from 'shared/web3';

interface UseLiveGameItemProps {
  currency: CurrencyType;
  userAddress: `0x${string}`;
  roomId: number;
  amount: number;
  containerRef: React.RefObject<HTMLDivElement>;
  joinRoomHandler: (roomId: number, amount: number) => void;
}

const useLiveGameItem = ({
  currency,
  userAddress,
  roomId,
  amount,
  containerRef,
  joinRoomHandler,
}: UseLiveGameItemProps) => {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const { currentAllowance, fetchCurrentAllowance, approveAllowance } = useStartGameStore();
  const { selectedCurrency } = useCurrenciesStore();

  const publicClient = usePublicClient();
  const { setSnapshotPoints } = useQuestsStore();

  const provider = usePublicClient();
  const walletClient = useWalletClient();

  const { chainId, address } = useAccount();
  const decimals = currency?.decimals ?? 18;
  const normalizedAmount = amount / 10 ** decimals;

  useEffect(() => {
    if (currency && userAddress && provider && currency.chainId === chainId && roomId) {
      fetchCurrentAllowance(address, selectedCurrency, publicClient);
    }
  }, [provider, currency, userAddress, chainId, roomId]);
  const handleShowError = (errorMessage: string) => {
    setIsError(true);
    setErrorMessage(errorMessage);

    setTimeout(() => {
      setIsError(false);
    }, 2000);
  };
  const handleJoinGame = async () => {
    if (selectedCurrency && selectedCurrency.balance >= normalizedAmount) {
      setLoading(true);
      try {
        if (Number(currentAllowance) < normalizedAmount) {
          await approveAllowance({
            amount,
            currency: selectedCurrency,
            userAddress: address as `0x${string}`,
            walletClient,
            publicClient,
            fullAllowance: false,
            joinGame: true,
          });
        }
        if (joinRoomHandler) {
          await joinRoomHandler(Number(roomId), amount);
        }
      } catch (error) {
        console.log('Error:', error);
      } finally {
        setSnapshotPoints();
        setLoading(false);
      }
    } else {
      handleShowError(t('pages.liveGamesSection.lowBalanceError'));
    }
  };
  const { isItemHovered, handleShowButton, handleHideButton, setIsItemHovered } = useHovered(
    containerRef,
    false,
    isError,
  );
  return {
    isItemHovered,
    isError,
    errorMessage,
    loading,
    handleShowButton,
    handleHideButton,
    handleJoinGame,
    normalizedAmount,
  };
};

export default useLiveGameItem;
